import React, { useRef, useEffect, useState, useCallback } from 'react';

export interface Annotation {
  type: 'circle' | 'arrow' | 'rectangle' | 'custom';
  x: number;
  y: number;
  width?: number;
  height?: number;
  radius?: number;
  endX?: number;
  endY?: number;
  points?: Array<{ x: number; y: number }>; // For custom shapes
  number?: number;
}

interface ScreenshotAnnotationCanvasProps {
  imageSrc: string;
  annotations: Annotation[];
  onAnnotationAdd: (annotation: Annotation) => void;
  onAnnotationRemove?: (number: number) => void;
  drawingMode: 'circle' | 'arrow' | 'rectangle' | 'custom' | null;
  zoomLevel?: number;
  onCanvasReady?: (getCanvasDataUrl: () => string | null) => void;
}

export const ScreenshotAnnotationCanvas: React.FC<
  ScreenshotAnnotationCanvasProps
> = ({
  imageSrc,
  annotations,
  onAnnotationAdd,
  onAnnotationRemove,
  drawingMode,
  zoomLevel = 1,
  onCanvasReady,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<{
    width: number;
    height: number;
    offsetX: number;
    offsetY: number;
    drawWidth: number;
    drawHeight: number;
  } | null>(null);
  const drawRetryCountRef = useRef<number>(0);
  const [canvasSize, setCanvasSize] = useState<{
    width: number;
    height: number;
  }>({ width: 1, height: 1 });
  const [displaySize, setDisplaySize] = useState<{
    width: number;
    height: number;
  }>({ width: 1, height: 1 });
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPos, setStartPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [currentPos, setCurrentPos] = useState<{ x: number; y: number } | null>(
    null
  );
  const [pathPoints, setPathPoints] = useState<Array<{ x: number; y: number }>>(
    []
  );

  const drawAnnotation = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      annotation: Annotation,
      isPreview = false
    ) => {
      ctx.strokeStyle = isPreview ? '#ff0000' : '#ff0000';
      ctx.fillStyle = isPreview
        ? 'rgba(255, 0, 0, 0.2)'
        : 'rgba(255, 0, 0, 0.2)';
      ctx.lineWidth = 2;

      switch (annotation.type) {
        case 'circle':
          if (annotation.radius) {
            ctx.beginPath();
            ctx.arc(
              annotation.x,
              annotation.y,
              annotation.radius,
              0,
              2 * Math.PI
            );
            ctx.stroke();
            ctx.fill();

            // Draw number inside circle (only for non-preview annotations)
            if (!isPreview && annotation.number !== undefined) {
              const savedFillStyle = ctx.fillStyle;
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 72px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(
                String(annotation.number),
                annotation.x,
                annotation.y
              );
              ctx.fillStyle = savedFillStyle;
            }
          }
          break;

        case 'rectangle':
          if (annotation.width && annotation.height) {
            ctx.strokeRect(
              annotation.x,
              annotation.y,
              annotation.width,
              annotation.height
            );
            ctx.fillRect(
              annotation.x,
              annotation.y,
              annotation.width,
              annotation.height
            );

            // Draw number inside rectangle (only for non-preview annotations)
            if (!isPreview && annotation.number !== undefined) {
              const savedFillStyle = ctx.fillStyle;
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 72px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(
                String(annotation.number),
                annotation.x + annotation.width / 2,
                annotation.y + annotation.height / 2
              );
              ctx.fillStyle = savedFillStyle;
            }
          }
          break;

        case 'arrow':
          if (annotation.endX !== undefined && annotation.endY !== undefined) {
            const startX = annotation.x;
            const startY = annotation.y;
            const endX = annotation.endX;
            const endY = annotation.endY;

            // Use thicker line for arrows
            const savedLineWidth = ctx.lineWidth;
            ctx.lineWidth = 5;

            // Draw line
            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, endY);
            ctx.stroke();

            // Draw arrowhead
            const angle = Math.atan2(endY - startY, endX - startX);
            const arrowLength = 15;
            const arrowAngle = Math.PI / 6;

            ctx.beginPath();
            ctx.moveTo(endX, endY);
            ctx.lineTo(
              endX - arrowLength * Math.cos(angle - arrowAngle),
              endY - arrowLength * Math.sin(angle - arrowAngle)
            );
            ctx.moveTo(endX, endY);
            ctx.lineTo(
              endX - arrowLength * Math.cos(angle + arrowAngle),
              endY - arrowLength * Math.sin(angle + arrowAngle)
            );
            ctx.stroke();

            // Restore original line width
            ctx.lineWidth = savedLineWidth;

            // Draw number near the arrow (at the midpoint) (only for non-preview annotations)
            if (!isPreview && annotation.number !== undefined) {
              const midX = (startX + endX) / 2;
              const midY = (startY + endY) / 2;
              const savedFillStyle = ctx.fillStyle;

              // Draw background circle for number (smaller for arrows)
              ctx.fillStyle = 'rgba(255, 0, 0, 0.8)';
              ctx.beginPath();
              ctx.arc(midX, midY, 40, 0, 2 * Math.PI);
              ctx.fill();

              // Draw number (smaller font for arrows)
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 56px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(String(annotation.number), midX, midY);
              ctx.fillStyle = savedFillStyle;
            }
          }
          break;

        case 'custom':
          if (annotation.points && annotation.points.length >= 3) {
            ctx.beginPath();
            // Move to first point
            ctx.moveTo(annotation.points[0].x, annotation.points[0].y);
            // Draw lines to all other points
            for (let i = 1; i < annotation.points.length; i++) {
              ctx.lineTo(annotation.points[i].x, annotation.points[i].y);
            }
            // Close the path
            ctx.closePath();
            ctx.stroke();
            ctx.fill();

            // Draw number inside the shape (only for non-preview annotations)
            if (!isPreview && annotation.number !== undefined) {
              // Calculate centroid of the shape
              let sumX = 0;
              let sumY = 0;
              for (const point of annotation.points) {
                sumX += point.x;
                sumY += point.y;
              }
              const centroidX = sumX / annotation.points.length;
              const centroidY = sumY / annotation.points.length;

              const savedFillStyle = ctx.fillStyle;
              ctx.fillStyle = '#ffffff';
              ctx.font = 'bold 72px Arial';
              ctx.textAlign = 'center';
              ctx.textBaseline = 'middle';
              ctx.fillText(String(annotation.number), centroidX, centroidY);
              ctx.fillStyle = savedFillStyle;
            }
          }
          break;
      }
    },
    []
  );

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      console.log('🖼️ Canvas not available');
      return;
    }
    if (!imageSrc || imageSrc.length < 100) {
      console.log('🖼️ Image source not available or too short');
      return;
    }

    // Reset retry count for new draw
    drawRetryCountRef.current = 0;

    console.log(
      '🖼️ Drawing image, src length:',
      imageSrc.length,
      'starts with:',
      imageSrc.substring(0, 50)
    );

    // Draw image
    const img = new Image();
    img.onerror = (error) => {
      console.error(
        '❌ Image load error:',
        error,
        'src length:',
        imageSrc.length
      );
    };
    img.onload = () => {
      // Get canvas again in case it changed
      const currentCanvas = canvasRef.current;
      if (!currentCanvas) {
        console.error('❌ Canvas lost during image load');
        return;
      }

      // Don't check dimensions here - just draw the image
      // The canvas will be sized based on the image, and CSS will scale it
      console.log('🖼️ Image loaded, proceeding with draw...');
      console.log(
        '🖼️ Image loaded successfully, dimensions:',
        img.width,
        'x',
        img.height
      );
      // Use the full image resolution for the canvas
      // This preserves quality when zooming
      // We'll use CSS to scale it down for initial display
      const maxCanvasSize = 4096; // Maximum canvas size (browsers have limits)

      // Calculate canvas size - use full image size if reasonable, otherwise scale down
      // For display, we'll limit to a reasonable size but keep high resolution for quality
      const maxDisplayWidth = 1920; // Max display width
      const maxDisplayHeight = 1080; // Max display height

      let canvasWidth = img.width;
      let canvasHeight = img.height;

      // Scale down if too large for canvas (browser limits)
      if (canvasWidth > maxCanvasSize || canvasHeight > maxCanvasSize) {
        const scale = Math.min(
          maxCanvasSize / canvasWidth,
          maxCanvasSize / canvasHeight
        );
        canvasWidth = Math.floor(img.width * scale);
        canvasHeight = Math.floor(img.height * scale);
      }

      // Calculate display size (for CSS) - ensure it fits in container
      // Container max width is typically around 896px (max-w-4xl = 56rem = 896px)
      // We'll use a large value to allow the CSS width: 100% to control the size
      const containerMaxWidth = 4000;
      let displayWidth = canvasWidth;
      let displayHeight = canvasHeight;

      // Scale down to fit container width while maintaining aspect ratio
      if (displayWidth > containerMaxWidth) {
        const scale = containerMaxWidth / displayWidth;
        displayWidth = containerMaxWidth;
        displayHeight = Math.floor(canvasHeight * scale);
      }

      // Also respect max height (but allow it to be taller if width requires it, let parent handle scroll/clip)
      // increasing this limit to allow better 100% width filling
      const containerMaxHeight = 4000;
      if (displayHeight > containerMaxHeight) {
        const scale = containerMaxHeight / displayHeight;
        displayHeight = containerMaxHeight;
        displayWidth = Math.floor(displayWidth * scale);
      }

      console.log('🖼️ Final display size:', displayWidth, 'x', displayHeight);

      // Only resize canvas if dimensions have changed
      // This prevents clearing the canvas unnecessarily
      const needsResize =
        currentCanvas.width !== canvasWidth ||
        currentCanvas.height !== canvasHeight;

      let ctx: CanvasRenderingContext2D | null;

      if (needsResize) {
        console.log(
          '🖼️ Resizing canvas from',
          currentCanvas.width,
          'x',
          currentCanvas.height,
          'to',
          canvasWidth,
          'x',
          canvasHeight
        );
        // Set canvas internal size (this clears the canvas, so we must draw immediately after)
        currentCanvas.width = canvasWidth;
        currentCanvas.height = canvasHeight;
        setCanvasSize({ width: canvasWidth, height: canvasHeight });
        setDisplaySize({ width: displayWidth, height: displayHeight });

        // Get context after resizing (context is lost when canvas size changes)
        ctx = currentCanvas.getContext('2d');
        if (!ctx) {
          console.error('❌ Failed to get canvas context after resize');
          return;
        }
        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      } else {
        console.log(
          '🖼️ Canvas size unchanged, skipping resize',
          canvasWidth,
          'x',
          canvasHeight
        );
        // Update display size state even if canvas size doesn't change
        setDisplaySize({ width: displayWidth, height: displayHeight });
        // Get context (no resize needed)
        ctx = currentCanvas.getContext('2d');
        if (!ctx) {
          console.error('❌ Failed to get canvas context');
          return;
        }
        // Enable high-quality image smoothing
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
      }

      // TypeScript type guard: ctx is guaranteed to be non-null after the checks above
      if (!ctx) return;

      // Draw image at full canvas resolution
      console.log('🖼️ Drawing image to canvas...');
      console.log('🖼️ Image dimensions:', img.width, 'x', img.height);
      console.log('🖼️ Canvas dimensions:', canvasWidth, 'x', canvasHeight);

      // No offsets needed since we're drawing at full size
      const offsetX = 0;
      const offsetY = 0;
      const drawWidth = canvasWidth;
      const drawHeight = canvasHeight;

      // Always draw the image scaled to canvas size FIRST
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
      console.log('🖼️ Drew image scaled to', canvasWidth, 'x', canvasHeight);

      // Store image dimensions and offsets for coordinate conversion
      imageRef.current = {
        width: img.width, // Original image width
        height: img.height, // Original image height
        offsetX,
        offsetY,
        drawWidth, // Display width (may be scaled down)
        drawHeight, // Display height (may be scaled down)
      };

      // Draw existing annotations AFTER the image
      annotations.forEach((annotation) => {
        // Convert from image coordinates (stored in original image size) to canvas coordinates (display size)
        const scaleX = drawWidth / img.width;
        const scaleY = drawHeight / img.height;
        const adjustedAnnotation = {
          ...annotation,
          x: annotation.x * scaleX + offsetX,
          y: annotation.y * scaleY + offsetY,
          width: annotation.width ? annotation.width * scaleX : undefined,
          height: annotation.height ? annotation.height * scaleY : undefined,
          radius: annotation.radius ? annotation.radius * scaleX : undefined,
          endX: annotation.endX
            ? annotation.endX * scaleX + offsetX
            : undefined,
          endY: annotation.endY
            ? annotation.endY * scaleY + offsetY
            : undefined,
          points: annotation.points
            ? annotation.points.map((point) => ({
                x: point.x * scaleX + offsetX,
                y: point.y * scaleY + offsetY,
              }))
            : undefined,
        };
        if (ctx) {
          drawAnnotation(ctx, adjustedAnnotation);
        }
      });

      // Draw preview annotation
      if (isDrawing && startPos && currentPos && drawingMode) {
        const previewAnnotation: Annotation = {
          type: drawingMode,
          x: startPos.x,
          y: startPos.y,
        };

        if (drawingMode === 'circle') {
          const radius = Math.sqrt(
            Math.pow(currentPos.x - startPos.x, 2) +
              Math.pow(currentPos.y - startPos.y, 2)
          );
          previewAnnotation.radius = radius;
        } else if (drawingMode === 'rectangle') {
          previewAnnotation.width = currentPos.x - startPos.x;
          previewAnnotation.height = currentPos.y - startPos.y;
        } else if (drawingMode === 'arrow') {
          previewAnnotation.endX = currentPos.x;
          previewAnnotation.endY = currentPos.y;
        } else if (drawingMode === 'custom') {
          // For custom shapes, use the path points collected so far
          // Add current position to show live preview
          const previewPoints = [...pathPoints];
          if (previewPoints.length > 0) {
            const lastPoint = previewPoints[previewPoints.length - 1];
            const distance = Math.sqrt(
              Math.pow(currentPos.x - lastPoint.x, 2) +
                Math.pow(currentPos.y - lastPoint.y, 2)
            );
            // Add current position if it's far enough from last point
            if (distance >= 5) {
              previewPoints.push(currentPos);
            }
          } else {
            previewPoints.push(startPos, currentPos);
          }
          // Auto-close by adding first point at the end
          if (previewPoints.length > 0) {
            previewPoints.push(previewPoints[0]);
          }
          previewAnnotation.points = previewPoints;
        }

        drawAnnotation(ctx, previewAnnotation, true);
      }

      // Verify the image was drawn by checking a pixel
      const testPixel = ctx.getImageData(0, 0, 1, 1);
      const pixelData = testPixel.data;
      const isBlank =
        pixelData[0] === 0 &&
        pixelData[1] === 0 &&
        pixelData[2] === 0 &&
        pixelData[3] === 0;
      console.log(
        '🖼️ Top-left pixel RGBA:',
        pixelData[0],
        pixelData[1],
        pixelData[2],
        pixelData[3],
        isBlank ? '(BLANK - will redraw)' : ''
      );

      // If pixel is blank (0,0,0,0), redraw the image
      // This can happen if the canvas was cleared after drawing
      const maxRetries = 3;
      if (isBlank && drawRetryCountRef.current < maxRetries) {
        drawRetryCountRef.current += 1;
        console.log(
          `🖼️ Canvas is blank, redrawing (attempt ${drawRetryCountRef.current}/${maxRetries})...`
        );
        // Redraw after a short delay to ensure canvas is ready
        setTimeout(() => {
          if (!ctx) return;
          ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
          // Draw annotations again
          annotations.forEach((annotation) => {
            const scaleX = drawWidth / img.width;
            const scaleY = drawHeight / img.height;
            const adjustedAnnotation = {
              ...annotation,
              x: annotation.x * scaleX + offsetX,
              y: annotation.y * scaleY + offsetY,
              width: annotation.width ? annotation.width * scaleX : undefined,
              height: annotation.height
                ? annotation.height * scaleY
                : undefined,
              radius: annotation.radius
                ? annotation.radius * scaleX
                : undefined,
              endX: annotation.endX
                ? annotation.endX * scaleX + offsetX
                : undefined,
              endY: annotation.endY
                ? annotation.endY * scaleY + offsetY
                : undefined,
              points: annotation.points
                ? annotation.points.map((point) => ({
                    x: point.x * scaleX + offsetX,
                    y: point.y * scaleY + offsetY,
                  }))
                : undefined,
            };
            if (ctx) {
              drawAnnotation(ctx, adjustedAnnotation);
            }
          });
          // Draw preview annotation if needed
          if (isDrawing && startPos && currentPos && drawingMode) {
            const previewAnnotation: Annotation = {
              type: drawingMode,
              x: startPos.x,
              y: startPos.y,
            };
            if (drawingMode === 'circle') {
              const radius = Math.sqrt(
                Math.pow(currentPos.x - startPos.x, 2) +
                  Math.pow(currentPos.y - startPos.y, 2)
              );
              previewAnnotation.radius = radius;
            } else if (drawingMode === 'rectangle') {
              previewAnnotation.width = currentPos.x - startPos.x;
              previewAnnotation.height = currentPos.y - startPos.y;
            } else if (drawingMode === 'arrow') {
              previewAnnotation.endX = currentPos.x;
              previewAnnotation.endY = currentPos.y;
            } else if (drawingMode === 'custom') {
              const previewPoints = [...pathPoints];
              if (previewPoints.length > 0) {
                const lastPoint = previewPoints[previewPoints.length - 1];
                const distance = Math.sqrt(
                  Math.pow(currentPos.x - lastPoint.x, 2) +
                    Math.pow(currentPos.y - lastPoint.y, 2)
                );
                if (distance >= 5) {
                  previewPoints.push(currentPos);
                }
              } else {
                previewPoints.push(startPos, currentPos);
              }
              if (previewPoints.length > 0) {
                previewPoints.push(previewPoints[0]);
              }
              previewAnnotation.points = previewPoints;
            }
            drawAnnotation(ctx, previewAnnotation, true);
          }
          // Verify again after redraw
          const verifyPixel = ctx.getImageData(0, 0, 1, 1);
          const verifyData = verifyPixel.data;
          const stillBlank =
            verifyData[0] === 0 &&
            verifyData[1] === 0 &&
            verifyData[2] === 0 &&
            verifyData[3] === 0;
          console.log(
            '🖼️ After redraw, pixel RGBA:',
            verifyData[0],
            verifyData[1],
            verifyData[2],
            verifyData[3],
            stillBlank ? '(STILL BLANK)' : '(SUCCESS)'
          );
        }, 10);
        return; // Exit early, will retry
      } else if (isBlank) {
        console.warn('⚠️ Canvas is still blank after', maxRetries, 'retries');
      } else {
        // Success - reset retry count
        drawRetryCountRef.current = 0;
      }

      // Verify the canvas has content
      const imageData = ctx.getImageData(
        0,
        0,
        Math.min(10, canvasWidth),
        Math.min(10, canvasHeight)
      );
      const hasContent = imageData.data.some(
        (value, index) => index % 4 !== 3 && value !== 0
      );
      console.log('🖼️ Canvas has content:', hasContent);
      console.log(
        '🖼️ Canvas element dimensions:',
        currentCanvas.offsetWidth,
        'x',
        currentCanvas.offsetHeight
      );
      console.log(
        '🖼️ Canvas computed style:',
        window.getComputedStyle(currentCanvas).display,
        window.getComputedStyle(currentCanvas).visibility
      );

      // Force a repaint to ensure the image is visible
      // This ensures the browser renders the canvas content
      void currentCanvas.offsetHeight; // Force reflow

      // Also trigger a small delay to ensure rendering
      requestAnimationFrame(() => {
        // Verify the image is still there
        const verifyCtx = currentCanvas.getContext('2d');
        if (!verifyCtx) return;

        const verifyPixel = verifyCtx.getImageData(0, 0, 1, 1);
        const verifyData = verifyPixel.data;
        const isBlankAfterDraw =
          verifyData[0] === 0 &&
          verifyData[1] === 0 &&
          verifyData[2] === 0 &&
          verifyData[3] === 0;

        console.log(
          '🖼️ Verification pixel after draw:',
          verifyData[0],
          verifyData[1],
          verifyData[2],
          verifyData[3],
          isBlankAfterDraw ? '(BLANK - will redraw)' : ''
        );

        // If pixel is blank after draw, redraw immediately
        if (isBlankAfterDraw && drawRetryCountRef.current < 3) {
          drawRetryCountRef.current += 1;
          console.log(
            `🖼️ Canvas blank after draw, redrawing (attempt ${drawRetryCountRef.current}/3)...`
          );
          // Redraw after a short delay
          setTimeout(() => {
            const redrawCtx = currentCanvas.getContext('2d');
            if (!redrawCtx) return;

            // Redraw the image
            redrawCtx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

            // Draw annotations again
            annotations.forEach((annotation) => {
              const scaleX = drawWidth / img.width;
              const scaleY = drawHeight / img.height;
              const adjustedAnnotation = {
                ...annotation,
                x: annotation.x * scaleX + offsetX,
                y: annotation.y * scaleY + offsetY,
                width: annotation.width ? annotation.width * scaleX : undefined,
                height: annotation.height
                  ? annotation.height * scaleY
                  : undefined,
                radius: annotation.radius
                  ? annotation.radius * scaleX
                  : undefined,
                endX: annotation.endX
                  ? annotation.endX * scaleX + offsetX
                  : undefined,
                endY: annotation.endY
                  ? annotation.endY * scaleY + offsetY
                  : undefined,
                points: annotation.points
                  ? annotation.points.map((point) => ({
                      x: point.x * scaleX + offsetX,
                      y: point.y * scaleY + offsetY,
                    }))
                  : undefined,
              };
              drawAnnotation(redrawCtx, adjustedAnnotation);
            });

            // Draw preview annotation if needed
            if (isDrawing && startPos && currentPos && drawingMode) {
              const previewAnnotation: Annotation = {
                type: drawingMode,
                x: startPos.x,
                y: startPos.y,
              };
              if (drawingMode === 'circle') {
                const radius = Math.sqrt(
                  Math.pow(currentPos.x - startPos.x, 2) +
                    Math.pow(currentPos.y - startPos.y, 2)
                );
                previewAnnotation.radius = radius;
              } else if (drawingMode === 'rectangle') {
                previewAnnotation.width = currentPos.x - startPos.x;
                previewAnnotation.height = currentPos.y - startPos.y;
              } else if (drawingMode === 'arrow') {
                previewAnnotation.endX = currentPos.x;
                previewAnnotation.endY = currentPos.y;
              } else if (drawingMode === 'custom') {
                // For custom shapes, use the path points collected so far
                const previewPoints = [...pathPoints];
                if (previewPoints.length > 0) {
                  const lastPoint = previewPoints[previewPoints.length - 1];
                  const distance = Math.sqrt(
                    Math.pow(currentPos.x - lastPoint.x, 2) +
                      Math.pow(currentPos.y - lastPoint.y, 2)
                  );
                  if (distance >= 5) {
                    previewPoints.push(currentPos);
                  }
                } else {
                  previewPoints.push(startPos, currentPos);
                }
                if (previewPoints.length > 0) {
                  previewPoints.push(previewPoints[0]);
                }
                previewAnnotation.points = previewPoints;
              }
              drawAnnotation(redrawCtx, previewAnnotation, true);
            }

            // Verify again
            const finalPixel = redrawCtx.getImageData(0, 0, 1, 1);
            const finalData = finalPixel.data;
            const stillBlank =
              finalData[0] === 0 &&
              finalData[1] === 0 &&
              finalData[2] === 0 &&
              finalData[3] === 0;
            console.log(
              '🖼️ After redraw in RAF, pixel RGBA:',
              finalData[0],
              finalData[1],
              finalData[2],
              finalData[3],
              stillBlank ? '(STILL BLANK)' : '(SUCCESS)'
            );

            if (!stillBlank) {
              drawRetryCountRef.current = 0; // Reset on success
            }
          }, 10);
        } else if (!isBlankAfterDraw) {
          drawRetryCountRef.current = 0; // Reset on success
        }
      });
    };
    img.src = imageSrc;
    console.log('🖼️ Set image src, waiting for load...');
  }, [
    imageSrc,
    annotations,
    isDrawing,
    startPos,
    currentPos,
    drawingMode,
    drawAnnotation,
    pathPoints,
  ]);

  // Expose canvas export function to parent
  useEffect(() => {
    if (onCanvasReady) {
      onCanvasReady(() => {
        const canvas = canvasRef.current;
        if (canvas) {
          // Return the canvas as data URL (includes annotations)
          return canvas.toDataURL('image/png');
        }
        return null;
      });
    }
  }, [onCanvasReady]);

  // Draw when imageSrc changes - always try to draw, don't wait for canvas dimensions
  useEffect(() => {
    console.log(
      '🖼️ useEffect triggered, imageSrc:',
      imageSrc ? 'exists' : 'null',
      'length:',
      imageSrc?.length,
      'starts with:',
      imageSrc?.substring(0, 50)
    );
    if (imageSrc && imageSrc.length > 100) {
      const canvas = canvasRef.current;
      if (!canvas) {
        console.log('🖼️ Canvas not available yet, will retry...');
        // Retry multiple times with increasing delays
        let attempts = 0;
        const maxAttempts = 10;
        const tryDraw = () => {
          attempts++;
          const currentCanvas = canvasRef.current;
          if (currentCanvas) {
            console.log(
              '🖼️ Canvas available after',
              attempts,
              'attempt(s), calling draw()'
            );
            draw();
          } else if (attempts < maxAttempts) {
            setTimeout(tryDraw, 50 * attempts);
          } else {
            console.error(
              '🖼️ Canvas not available after',
              maxAttempts,
              'attempts'
            );
          }
        };
        const timer = setTimeout(tryDraw, 50);
        return () => clearTimeout(timer);
      }

      // Use multiple requestAnimationFrame to ensure DOM is ready
      const rafId1 = requestAnimationFrame(() => {
        const rafId2 = requestAnimationFrame(() => {
          console.log('🖼️ Calling draw() after RAF');
          draw();
        });
        return () => cancelAnimationFrame(rafId2);
      });
      return () => cancelAnimationFrame(rafId1);
    }
  }, [draw, imageSrc]);

  // Also redraw when drawingMode or annotations change (this is why it works when you click a button)
  useEffect(() => {
    if (imageSrc && imageSrc.length > 100) {
      console.log('🖼️ drawingMode or annotations changed, forcing redraw');
      // Small delay to ensure state is updated
      const timer = setTimeout(() => {
        draw();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [drawingMode, annotations, draw, imageSrc]);

  const getCanvasCoordinates = (event: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    // getBoundingClientRect() already accounts for CSS transform scale
    // So we just need to convert from displayed size to canvas size
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    // Convert mouse coordinates to canvas coordinates accounting for scaling
    const x = (event.clientX - rect.left) * scaleX;
    const y = (event.clientY - rect.top) * scaleY;

    return { x, y };
  };

  const handleMouseDown = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Only handle drawing if in drawing mode
    // Otherwise, let the parent handle panning (don't stop propagation)
    if (!drawingMode) {
      return; // Let event bubble up to parent for panning
    }

    const pos = getCanvasCoordinates(event);
    if (pos) {
      setIsDrawing(true);
      setStartPos(pos);
      setCurrentPos(pos);
      // For custom shapes, initialize path with first point
      if (drawingMode === 'custom') {
        setPathPoints([pos]);
      } else {
        setPathPoints([]);
      }
    }
    event.stopPropagation(); // Only stop when actually drawing
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLCanvasElement>) => {
    // Only handle drawing if in drawing mode
    if (!drawingMode) {
      return; // Let event bubble up to parent for panning
    }

    if (!isDrawing) return;

    const pos = getCanvasCoordinates(event);
    if (pos) {
      setCurrentPos(pos);
      // For custom shapes, add point to path
      if (drawingMode === 'custom') {
        setPathPoints((prev) => {
          // Only add if point is far enough from last point (to avoid too many points)
          if (prev.length === 0) {
            return [pos];
          }
          const lastPoint = prev[prev.length - 1];
          const distance = Math.sqrt(
            Math.pow(pos.x - lastPoint.x, 2) + Math.pow(pos.y - lastPoint.y, 2)
          );
          // Add point if it's at least 5 pixels away from last point
          if (distance >= 5) {
            return [...prev, pos];
          }
          return prev;
        });
      }
    }
    event.stopPropagation(); // Only stop when actually drawing
  };

  const handleMouseUp = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !startPos || !drawingMode || !imageRef.current) return;

    const pos = getCanvasCoordinates(event);
    if (pos) {
      const img = imageRef.current;

      // Convert canvas coordinates to image coordinates
      const imageStartX =
        (startPos.x - img.offsetX) * (img.width / img.drawWidth);
      const imageStartY =
        (startPos.y - img.offsetY) * (img.height / img.drawHeight);
      const imageEndX = (pos.x - img.offsetX) * (img.width / img.drawWidth);
      const imageEndY = (pos.y - img.offsetY) * (img.height / img.drawHeight);

      // Minimum size constants (in image coordinates)
      const MIN_CIRCLE_RADIUS = 30;
      const MIN_RECTANGLE_SIZE = 60; // minimum width and height
      const MIN_ARROW_DISTANCE = 60; // minimum distance between start and end
      const MIN_CUSTOM_POINTS = 3; // minimum number of points for custom shape

      const annotation: Annotation = {
        type: drawingMode,
        x: imageStartX,
        y: imageStartY,
      };

      let isValid = false;

      if (drawingMode === 'circle') {
        const radius = Math.sqrt(
          Math.pow(imageEndX - imageStartX, 2) +
            Math.pow(imageEndY - imageStartY, 2)
        );
        if (radius >= MIN_CIRCLE_RADIUS) {
          annotation.radius = radius;
          isValid = true;
        }
      } else if (drawingMode === 'rectangle') {
        const width = imageEndX - imageStartX;
        const height = imageEndY - imageStartY;
        if (
          Math.abs(width) >= MIN_RECTANGLE_SIZE &&
          Math.abs(height) >= MIN_RECTANGLE_SIZE
        ) {
          annotation.width = width;
          annotation.height = height;
          isValid = true;
        }
      } else if (drawingMode === 'arrow') {
        const distance = Math.sqrt(
          Math.pow(imageEndX - imageStartX, 2) +
            Math.pow(imageEndY - imageStartY, 2)
        );
        if (distance >= MIN_ARROW_DISTANCE) {
          annotation.endX = imageEndX;
          annotation.endY = imageEndY;
          isValid = true;
        }
      } else if (drawingMode === 'custom') {
        // Convert path points to image coordinates
        const imagePoints = pathPoints.map((point) => ({
          x: (point.x - img.offsetX) * (img.width / img.drawWidth),
          y: (point.y - img.offsetY) * (img.height / img.drawHeight),
        }));
        // Auto-close by adding first point at the end
        if (imagePoints.length > 0) {
          imagePoints.push(imagePoints[0]);
        }
        // Check minimum points requirement
        if (imagePoints.length >= MIN_CUSTOM_POINTS) {
          annotation.points = imagePoints;
          isValid = true;
        }
      }

      // Only add annotation if it meets minimum size requirements
      if (isValid) {
        onAnnotationAdd(annotation);
      }

      setIsDrawing(false);
      setStartPos(null);
      setCurrentPos(null);
      setPathPoints([]);
    }
  };

  return (
    <canvas
      ref={canvasRef}
      width={canvasSize.width}
      height={canvasSize.height}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => {
        setIsDrawing(false);
        setStartPos(null);
        setCurrentPos(null);
      }}
      style={{
        cursor: drawingMode ? 'crosshair' : 'inherit',
        width: '100%',
        maxWidth: '100%',
        height: 'auto',
        display: 'block',
        pointerEvents: drawingMode
          ? 'auto'
          : onAnnotationRemove
          ? 'auto'
          : 'none',
        visibility: 'visible',
        opacity: 1,
        imageRendering: 'auto',
      }}
    />
  );
};
